import { useMemo, useState } from 'react';
import { formatTime } from '../game/state.ts';
import { computeStats, formatAverage, type TimeStats } from '../game/stats.ts';
import { LEVEL_INFO } from '../game/types.ts';
import { storage } from '../storage/storage.ts';
import { ConfirmDialog } from './Dialogs.tsx';
import { BackIcon } from './Icons.tsx';

const time = (ms: number | null) => (ms === null ? '–' : formatTime(ms));

function Row({ label, s }: { label: string; s: TimeStats }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>{s.solved}</td>
      <td>{time(s.bestMs)}</td>
      <td>{time(s.averageMs === null ? null : Math.round(s.averageMs))}</td>
    </tr>
  );
}

/** Completed games per level on this device, split by Auto notes. */
export function Stats({ onBack }: { onBack: () => void }) {
  const [version, setVersion] = useState(0);
  const [resetStep, setResetStep] = useState<0 | 1 | 2>(0);
  const [done, setDone] = useState(false);
  const stats = useMemo(() => {
    const { solves, starts } = storage.loadStats();
    return computeStats(solves, starts);
    // `version` re-reads storage after a reset.
  }, [version]);
  const anything = stats.some((s) => s.solved || s.unfinished);

  return (
    <div className="stats">
      <header className="stats-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back to levels">
          <BackIcon />
        </button>
        <h1>Stats</h1>
      </header>
      <p className="stats-intro">Completed games on this device. Hints and checks are averages per completed game.</p>
      {done ? (
        <p className="stats-done" role="status">
          Stats reset.
        </p>
      ) : null}

      {stats.map((s) => (
        <section key={s.level} className="stats-level" aria-labelledby={`stats-${s.level}`}>
          <h2 id={`stats-${s.level}`}>{LEVEL_INFO[s.level].name}</h2>
          {s.solved ? (
            <>
              <div className="stats-table-wrap">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th scope="col">
                        <span className="visually-hidden">Auto notes</span>
                      </th>
                      <th scope="col">Solved</th>
                      <th scope="col">Best</th>
                      <th scope="col">Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    <Row label="Without auto notes" s={s.withoutAutoNotes} />
                    <Row label="With auto notes" s={s.withAutoNotes} />
                  </tbody>
                </table>
              </div>
              <p className="stats-extra">
                {formatAverage(s.averageHints!)} hints and {formatAverage(s.averageChecks!)} checks per game
              </p>
            </>
          ) : (
            <p className="stats-empty">No completed games yet.</p>
          )}
          {s.unfinished ? (
            <p className="stats-note">
              {s.unfinished} started, not finished. Not included above.
            </p>
          ) : null}
        </section>
      ))}

      {anything ? (
        <button type="button" className="btn danger stats-reset" onClick={() => setResetStep(1)}>
          Reset stats
        </button>
      ) : null}

      <ConfirmDialog
        open={resetStep === 1}
        title="Reset your stats?"
        body="This clears completed games, best and average times, and hint and check averages for every level. Games in progress are kept."
        confirmLabel="Continue"
        onConfirm={() => setResetStep(2)}
        onCancel={() => setResetStep(0)}
      />
      <ConfirmDialog
        open={resetStep === 2}
        title="This can't be undone"
        body="All your stats will be permanently deleted and cannot be restored. Reset them now?"
        confirmLabel="Reset stats"
        cancelLabel="Keep my stats"
        danger
        onConfirm={() => {
          storage.resetStats();
          setResetStep(0);
          setDone(true);
          setVersion((v) => v + 1);
        }}
        onCancel={() => setResetStep(0)}
      />
    </div>
  );
}
